import { getCurrentInstance, nextTick, reactive } from 'vue'
import { watchDebounced } from '@vueuse/core'

export type PreviewOverflowWatchSource = {
  name: string
  source: () => unknown
}

type HostRefs = Record<string, unknown>

function asMeasureEl(el: unknown): HTMLElement | undefined {
  if (el == null || typeof el !== 'object') return undefined
  const node = el as {
    clientHeight?: unknown
    scrollHeight?: unknown
    $el?: unknown
  }
  if (
    typeof node.clientHeight === 'number' &&
    typeof node.scrollHeight === 'number'
  ) {
    return el as HTMLElement
  }
  if (node.$el) return asMeasureEl(node.$el)
  return undefined
}

function resolveMeasureEl(el: unknown): HTMLElement | undefined {
  if (Array.isArray(el)) {
    for (const item of el) {
      const measured = resolveMeasureEl(item)
      if (measured) return measured
    }
    return undefined
  }
  return asMeasureEl(el)
}

function overflowHeight(el: HTMLElement): number {
  return Math.max(el.clientHeight, el.scrollHeight)
}

export function usePreviewOverflow(options: {
  getEl: (name: string) => unknown
  maxHeights: Record<string, number>
  watchSources?: PreviewOverflowWatchSource[]
}) {
  const classObject = reactive<Record<string, Record<string, boolean>>>({})
  const errorMessages = reactive<Record<string, string>>({})
  const instance = getCurrentInstance()

  function ensureKeys(name: string) {
    if (!(name in classObject)) {
      classObject[name] = {}
    }
    if (!(name in errorMessages)) {
      errorMessages[name] = ''
    }
  }

  for (const name of Object.keys(options.maxHeights)) {
    ensureKeys(name)
  }

  function getMeasureEl(refName: string): HTMLElement | undefined {
    const fromGetter = resolveMeasureEl(options.getEl(refName))
    if (fromGetter) return fromGetter
    const refs = instance?.proxy?.$refs as HostRefs | undefined
    return resolveMeasureEl(refs?.[refName])
  }

  function adjustFontSize(refName: string) {
    ensureKeys(refName)
    classObject[refName] = {}
    errorMessages[refName] = ''
    nextTick(() => {
      const el = getMeasureEl(refName)
      if (!el) return
      const maxHeight = options.maxHeights[refName]
      if (maxHeight == null) return
      if (overflowHeight(el) > maxHeight) {
        classObject[refName] = {
          'font-size-sm': true
        }
      } else {
        classObject[refName] = {}
      }
      nextTick(() => {
        if (overflowHeight(el) > maxHeight) {
          errorMessages[refName] = '入力欄をオーバーしてます'
          classObject[refName] = { 'font-size-sm': true, error: true }
        }
      })
    })
  }

  if (options.watchSources) {
    for (const item of options.watchSources) {
      watchDebounced(
        item.source,
        () => {
          adjustFontSize(item.name)
        },
        { debounce: 500 }
      )
    }
  }

  return {
    classObject,
    errorMessages,
    adjustFontSize
  }
}
